import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { ArrowUp, Phone, MapPin } from 'lucide-react'
import { UP, STAGGER } from '@/lib/motion'

export function Footer() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <footer
      ref={ref}
      style={{
        background: 'var(--black)',
        borderTop: '1px solid rgba(232,255,0,0.08)',
        padding: '80px max(5vw, 24px) 40px'
      }}
    >
      <motion.div
        variants={STAGGER(0.1)}
        initial="hidden"
        animate={isInView ? 'show' : 'hidden'}
        style={{
          maxWidth: 1400,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 60
        }}
      >
        {/* Logo & Tagline */}
        <motion.div variants={UP}>
          <a
            href="/"
            style={{
              fontFamily: 'Bebas Neue',
              fontSize: '2.5rem',
              color: 'var(--white)',
              textDecoration: 'none',
              display: 'flex',
              flexDirection: 'column',
              lineHeight: 1,
              marginBottom: 16
            }}
          >
            <span>
              A<span style={{ color: 'var(--lime)' }}>SHU</span>
            </span>
          </a>
          <p
            style={{
              fontFamily: 'DM Sans',
              fontSize: '0.9rem',
              color: 'var(--white-30)',
              marginBottom: 8
            }}
          >
            አሹ ያጸዳዎታል ✦
          </p>
          <p
            style={{
              fontFamily: 'DM Sans',
              fontSize: '0.85rem',
              color: 'var(--white-30)',
              lineHeight: 1.6
            }}
          >
            Professional cleaning service in Addis Ababa, Ethiopia
          </p>
        </motion.div>

        {/* Quick Links */}
        <motion.div variants={UP}>
          <h4
            style={{
              fontFamily: 'DM Sans',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: 'var(--white)',
              marginBottom: 20,
              letterSpacing: '0.1em',
              textTransform: 'uppercase'
            }}
          >
            Quick Links
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {['Home', 'Services', 'About', 'Gallery', 'Contact', 'Book Now'].map((link) => (
              <a
                key={link}
                href={`/${link.toLowerCase().replace(' ', '-')}`}
                style={{
                  fontFamily: 'DM Sans',
                  fontSize: '0.9rem',
                  color: 'var(--white-60)',
                  textDecoration: 'none',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--lime)')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--white-60)')}
              >
                {link}
              </a>
            ))}
          </div>
        </motion.div>

        {/* Contact Info */}
        <motion.div variants={UP}>
          <h4
            style={{
              fontFamily: 'DM Sans',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: 'var(--white)',
              marginBottom: 20,
              letterSpacing: '0.1em',
              textTransform: 'uppercase'
            }}
          >
            Contact Us
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <a
              href="tel:+251997745317"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontFamily: 'DM Sans',
                fontSize: '1.1rem',
                color: 'var(--lime)',
                textDecoration: 'none',
                fontWeight: 700
              }}
            >
              <Phone size={18} />
              +251 99 774 5317
            </a>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontFamily: 'DM Sans',
                fontSize: '0.9rem',
                color: 'var(--white-60)'
              }}
            >
              <MapPin size={18} />
              Addis Ababa, Ethiopia
            </div>
          </div>
        </motion.div>

        {/* Social & Back to Top */}
        <motion.div variants={UP}>
          <h4
            style={{
              fontFamily: 'DM Sans',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: 'var(--white)',
              marginBottom: 20,
              letterSpacing: '0.1em',
              textTransform: 'uppercase'
            }}
          >
            Follow Us
          </h4>
          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            {['Telegram', 'Instagram', 'Facebook'].map((social) => (
              <motion.a
                key={social}
                href="#"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: 'var(--surface)',
                  border: '1px solid rgba(232,255,0,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  color: 'var(--white-60)',
                  textDecoration: 'none'
                }}
              >
                {social[0]}
              </motion.a>
            ))}
          </div>

          {/* Scroll to top */}
          <motion.button
            onClick={scrollToTop}
            data-cursor="hover"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'var(--lime)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--black)'
            }}
          >
            <ArrowUp size={20} />
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Copyright */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ delay: 0.5 }}
        style={{
          maxWidth: 1400,
          margin: '60px auto 0',
          paddingTop: 30,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          textAlign: 'center',
          fontFamily: 'DM Sans',
          fontSize: '0.8rem',
          color: 'var(--white-30)'
        }}
      >
        © 2024 Ashu Cleaning Service. አሹ የፅዳት አገልግሎት. All rights reserved.
      </motion.div>
    </footer>
  )
}
